import styled, { css } from 'styled-components';
import { lighten } from 'polished';
import {
  ButtonAppearance,
  ButtonShape,
  ButtonSize,
} from '@/components/UI/Button/Button.types';

function getHeight(height: number) {
  return css`
    height: ${height}px;
  `;
}

function getWidth(
  shape?: ButtonShape,
  isFullWidth?: boolean,
  isAutoWidth?: boolean,
  height?: number
) {
  switch (true) {
    case isAutoWidth: {
      return 'auto';
    }
    case shape === 'square': {
      return `${height ?? 40}px`;
    }
    case isFullWidth: {
      return '100%';
    }
    default: {
      return 'auto';
    }
  }
}

function getPadding(
  shape?: ButtonShape,
  hasPadding?: boolean,
  isLoading?: boolean
) {
  switch (true) {
    case shape === 'square' || !hasPadding: {
      return 0;
    }
    case isLoading: {
      return '15px 16px';
    }

    default: {
      return '16px';
    }
  }
}

function getBorderRadius(borderRadius?: number) {
  return borderRadius ?? 7;
}

function getStylesForAppearance(appearance?: ButtonAppearance) {
  switch (true) {
    case appearance === 'transparent': {
      return css`
        background-color: transparent;
        border: 0;
      `;
    }
    case appearance === 'primary': {
      return css`
        background-color: ${({ theme }) => theme.palette.brand.main};
        border: 0;
        color: ${({ theme }) => theme.palette.system.white};

        &:disabled {
          background-color: ${({ theme }) =>
            lighten(0.2, theme.palette.brand.main)};
        }
      `;
    }
    case appearance === 'primaryOutlined': {
      return css`
        background-color: ${({ theme }) => theme.palette.primary.background};
        border-color: ${({ theme }) => theme.palette.brand.main};
        color: ${({ theme }) => theme.palette.brand.main};
      `;
    }
    case appearance === 'primaryText': {
      return css`
        background-color: ${({ theme }) => theme.palette.primary.background};
        border-color: transparent;
        color: ${({ theme }) => theme.palette.brand.main};
      `;
    }
    case appearance === 'secondary': {
      return css`
        background-color: ${({ theme }) => theme.palette.system.blue};
        border: 0;
        color: ${({ theme }) => theme.palette.system.white};

        &:disabled {
          background-color: ${({ theme }) =>
            lighten(0.1, theme.palette.system.blue)};
        }

        @media (hover: hover) {
          &:hover {
            background-color: ${({ theme }) => theme.palette.system.deepBlue};
          }
        }
      `;
    }
    case appearance === 'secondaryLight': {
      return css`
        background-color: ${({ theme }) => theme.palette.system.lightBlue};
        border: 0;
        color: ${({ theme }) => theme.palette.system.blue};

        &:disabled {
          background-color: ${({ theme }) =>
            lighten(0.02, theme.palette.system.lightBlue)};
        }
      `;
    }
    case appearance === 'secondaryOutlined': {
      return css`
        background-color: ${({ theme }) => theme.palette.primary.background};
        border-color: ${({ theme }) => theme.palette.system.blue};
        color: ${({ theme }) => theme.palette.system.blue};
      `;
    }
    case appearance === 'secondaryText': {
      return css`
        background-color: ${({ theme }) => theme.palette.primary.background};
        border-color: transparent;
        color: ${({ theme }) => theme.palette.system.blue};
      `;
    }
    case appearance === 'secondaryWhite': {
      return css`
        color: ${({ theme }) => theme.palette.system.blue};
      `;
    }
    case appearance === 'green': {
      return css`
        background-color: ${({ theme }) => theme.palette.system.green};
        border-color: transparent;
        color: ${({ theme }) => theme.palette.system.white};
      `;
    }
    case appearance === 'red': {
      return css`
        background-color: ${({ theme }) => theme.palette.system.red};
        border-color: transparent;
        color: ${({ theme }) => theme.palette.system.white};

        &:disabled {
          background-color: ${({ theme }) =>
            lighten(0.2, theme.palette.system.red)};
        }
      `;
    }
    case appearance === 'redOutlined': {
      return css`
        background-color: ${({ theme }) => theme.palette.primary.background};
        border-color: ${({ theme }) => theme.palette.system.red};
        color: ${({ theme }) => theme.palette.system.red};
      `;
    }
    case appearance === 'gray': {
      return css`
        background-color: transparent;
        border-color: transparent;
        color: ${({ theme }) => theme.palette.secondary.labels};
      `;
    }
    case appearance === 'defaultOutlined': {
      return css`
        background-color: transparent;
      `;
    }

    default: {
      return css``;
    }
  }
}

function getStylesForSize(size?: ButtonSize, hasPadding?: boolean) {
  switch (true) {
    case !hasPadding: {
      return css`
        height: auto;
      `;
    }
    case size === 's': {
      return css`
        height: 40px;
        padding: 10px;
      `;
    }
    case size === 'm': {
      return css`
        height: 50px;
      `;
    }
    case size === 'l': {
      return css`
        height: 60px;
      `;
    }
    default: {
      return css``;
    }
  }
}

export const ButtonBox = styled.button<{
  $shape?: ButtonShape;
  $appearance?: ButtonAppearance;
  $isFullWidth?: boolean;
  $isAutoWidth?: boolean;
  $hasPadding?: boolean;
  $isLoading?: boolean;
  $size?: ButtonSize;
  $borderRadius?: number;
  $height?: number;
  $isBodyFontFamily?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: ${({ $shape, $isFullWidth, $isAutoWidth, $height }) =>
    getWidth($shape, $isFullWidth, $isAutoWidth, $height)};
  padding: ${({ $shape, $hasPadding, $isLoading }) =>
    getPadding($shape, $hasPadding, $isLoading)};
  font-size: 15px;
  font-weight: 400;
  font-family: inherit;
  line-height: 1.2;
  border-radius: ${({ $borderRadius }) => getBorderRadius($borderRadius)}px;
  background-color: ${({ theme }) => theme.palette.secondary.background};
  border: 1px solid ${({ theme }) => theme.palette.primary.border};
  appearance: none;
  cursor: pointer;

  ${({ $appearance }) => getStylesForAppearance($appearance)}

  ${({ $height }) => ($height ? getHeight($height) : '')};
  ${({ $hasPadding, $size, $height }) =>
    !$height && getStylesForSize($size, $hasPadding)}

  ${({ $shape }) =>
    $shape === 'square' &&
    css`
      aspect-ratio: 1;
    `}

  ${({ $isFullWidth }) =>
    $isFullWidth &&
    css`
      flex-shrink: 1;
    `}

  ${({ $isBodyFontFamily }) =>
    $isBodyFontFamily &&
    css`
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    `}
`;
